import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { db, analytics } from '../api/firebase';
import {
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Box,
  Rating,
  Grid,
  Button,
  LinearProgress,
  Breadcrumbs,
  Chip,
  Container,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  RateReview as RateReviewIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ReviewList from '../components/ReviewList';
import AddReviewForm from '../components/AddReviewForm';

const LecturerPage = () => {
  const { professorId } = useParams();
  const { user } = useAuth();
  const [lecturer, setLecturer] = useState(null);
  const [department, setDepartment] = useState(null);
  const [loadingLecturer, setLoadingLecturer] = useState(true);
  const [errorLecturer, setErrorLecturer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [loadingMoreReviews, setLoadingMoreReviews] = useState(false);
  const [errorReviews, setErrorReviews] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMoreReviews, setHasMoreReviews] = useState(true);
  const { t, isArabic } = useLanguage();

  const REVIEWS_PER_PAGE = 20;

  useEffect(() => {
    const fetchLecturer = async () => {
      if (!professorId) {
        setErrorLecturer('Lecturer ID is missing.');
        setLoadingLecturer(false);
        return;
      }

      setLoadingLecturer(true);
      setErrorLecturer(null);
      try {
        const lecturerDocRef = doc(db, 'professors', professorId);
        const docSnap = await getDoc(lecturerDocRef);

        if (docSnap.exists()) {
          const lecturerData = { id: docSnap.id, ...docSnap.data() };
          setLecturer(lecturerData);
          
          // Fetch department name
          if (lecturerData.departmentId) {
            const deptDoc = await getDoc(doc(db, 'departments', lecturerData.departmentId));
            if (deptDoc.exists()) {
              setDepartment(deptDoc.data());
            }
          }
          
          logEvent(analytics, 'view_lecturer', {
            lecturer_id: professorId,
            lecturer_name: lecturerData.full_name,
            department_id: lecturerData.departmentId
          });
        } else {
          setErrorLecturer('Lecturer not found.');
          setLecturer(null);
        }
      } catch (err) {
        setErrorLecturer('Failed to load lecturer details.');
      } finally {
        setLoadingLecturer(false);
      }
    };

    fetchLecturer();
  }, [professorId]);

  useEffect(() => {
    const fetchInitialReviews = async () => {
      if (!professorId) return;

      setLoadingReviews(true);
      setErrorReviews(null);

      try {
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('professorId', '==', professorId),
          orderBy('createdAt', 'desc'),
          limit(REVIEWS_PER_PAGE)
        );

        const querySnapshot = await getDocs(reviewsQuery);
        const reviewsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setReviews(reviewsList);
        
        if (querySnapshot.docs.length > 0) {
          setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
          setHasMoreReviews(querySnapshot.docs.length === REVIEWS_PER_PAGE);
        } else {
          setHasMoreReviews(false);
        }
      } catch (err) {
        if (err.code === 'failed-precondition') {
          setErrorReviews(t('Database index is being built. Please try again in a few minutes.', 'فهرس قاعدة البيانات قيد الإنشاء. يرجى المحاولة مرة أخرى خلال بضع دقائق.'));
        } else {
          setErrorReviews(t('Failed to load reviews.', 'فشل في تحميل المراجعات.'));
        }
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchInitialReviews();
  }, [professorId, t]);

  const loadMoreReviews = async () => {
    if (!lastVisible || loadingMoreReviews || !hasMoreReviews) return;

    setLoadingMoreReviews(true);
    try {
      const nextQuery = query(
        collection(db, 'reviews'),
        where('professorId', '==', professorId),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(REVIEWS_PER_PAGE)
      );

      const nextSnapshot = await getDocs(nextQuery);
      const nextReviewsList = nextSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setReviews(prev => [...prev, ...nextReviewsList]);

      if (nextSnapshot.docs.length > 0) {
        setLastVisible(nextSnapshot.docs[nextSnapshot.docs.length - 1]);
        setHasMoreReviews(nextSnapshot.docs.length === REVIEWS_PER_PAGE);
      } else {
        setHasMoreReviews(false);
      }
    } catch (err) {
      setErrorReviews(t('Failed to load more reviews.', 'فشل في تحميل المزيد من المراجعات.'));
    } finally {
      setLoadingMoreReviews(false);
    }
  };

  const averageRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + (review.overallRating || review.rating || 0), 0);
    return Math.round((sum / reviews.length) * 2) / 2;
  }, [reviews]);

  const criteriaAverages = useMemo(() => {
    if (reviews.length === 0) return {};
    
    const newCriteriaKeys = ['teachingQuality', 'treatment', 'gradingAssessment', 'tests', 'attendance'];
    const legacyCriteriaKeys = ['knowledgeExpertise', 'communication', 'assessment', 'studentEngagement'];
    const allKeys = [...newCriteriaKeys, ...legacyCriteriaKeys];
    
    return allKeys.reduce((acc, key) => {
      const validRatings = reviews
        .filter(review => review.ratings && review.ratings[key] > 0)
        .map(review => review.ratings[key]);
      
      if (validRatings.length > 0) {
        acc[key] = (validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length);
      }
      return acc;
    }, {});
  }, [reviews]);

  const criteriaLabels = {
    teachingQuality: t('Teaching Quality', 'جودة التدريس'),
    treatment: t('Treatment', 'المعاملة'),
    gradingAssessment: t('Grading/Assessment', 'التقييم والدرجات'),
    tests: t('Tests', 'الاختبارات'),
    attendance: t('Attendance', 'الحضور'),
    knowledgeExpertise: t('Knowledge & Expertise', 'المعرفة والخبرة'),
    communication: t('Communication', 'التواصل'),
    assessment: t('Assessment', 'التقييم'),
    studentEngagement: t('Student Engagement', 'إشراك الطلاب')
  };

  // Get color based on rating value
  const getRatingColor = (value) => {
    if (value >= 4) return '#22c55e'; // Green
    if (value >= 3) return '#eab308'; // Yellow
    if (value >= 2) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  if (loadingLecturer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (errorLecturer) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{errorLecturer}</Alert>
      </Container>
    );
  }

  if (!lecturer) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">{t('Lecturer data could not be loaded.', 'لا يمكن تحميل بيانات المحاضر.')}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      {/* Lecturer Header Card */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          py: { xs: 4, md: 6 },
          px: 2,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 3,
              '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' },
            }}
          >
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                {t('Home', 'الرئيسية')}
              </Typography>
            </Link>
            {lecturer.universityId && (
              <Link to={`/university/${lecturer.universityId}`} style={{ textDecoration: 'none' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                  {t('University', 'الجامعة')}
                </Typography>
              </Link>
            )}
            <Typography sx={{ color: 'white' }}>
              {lecturer.full_name || lecturer.name}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <Box
              sx={{
                width: { xs: 80, md: 120 },
                height: { xs: 80, md: 120 },
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ color: 'white', fontSize: { xs: 48, md: 72 } }} />
            </Box>

            {/* Lecturer Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  mb: 1,
                  lineHeight: 1.2,
                }}
              >
                {lecturer.full_name || lecturer.name}
              </Typography>

              {department && (
                <Chip
                  label={department.name}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 500,
                    mb: 2,
                  }}
                />
              )}

              {/* Rating Summary */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <StarIcon sx={{ color: '#facc15', fontSize: 28 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {averageRating > 0 ? averageRating.toFixed(1) : '-'}
                    </Typography>
                  </Box>
                  <Rating
                    value={averageRating}
                    precision={0.5}
                    readOnly
                    sx={{ direction: 'ltr', '& .MuiRating-iconFilled': { color: '#facc15' } }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RateReviewIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 20 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {reviews.length} {t('reviews', 'تقييم')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Detailed Ratings */}
        {Object.keys(criteriaAverages).length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              {t('Rating Breakdown', 'تفصيل التقييمات')}
            </Typography>
            <Grid container spacing={3}>
              {Object.entries(criteriaAverages).map(([key, average]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {criteriaLabels[key]}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: getRatingColor(average) }}>
                        {average.toFixed(1)}/5
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(average / 5) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: getRatingColor(average),
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Reviews Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            {t('Reviews', 'التقييمات')} ({reviews.length})
          </Typography>
          
          {loadingReviews && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          
          {errorReviews && <Alert severity="error" sx={{ mb: 2 }}>{errorReviews}</Alert>}
          
          {!loadingReviews && !errorReviews && (
            <>
              {reviews.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <RateReviewIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                  <Typography color="text.secondary">
                    {t('No reviews yet. Be the first to review!', 'لا توجد تقييمات. كن أول من يقيّم!')}
                  </Typography>
                </Box>
              ) : (
                <ReviewList reviews={reviews} showDelete={true} currentUserId={user?.uid} onReviewDeleted={() => {}} />
              )}
              
              {hasMoreReviews && reviews.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={loadMoreReviews}
                    disabled={loadingMoreReviews}
                    startIcon={loadingMoreReviews ? <CircularProgress size={20} /> : <ExpandMoreIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: 'none',
                    }}
                  >
                    {loadingMoreReviews ? t('Loading...', 'جاري التحميل...') : t('Load More', 'تحميل المزيد')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Add Review Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 3,
            bgcolor: 'rgba(30, 58, 138, 0.02)',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            {t('Share Your Experience', 'شارك تجربتك')}
          </Typography>
          <AddReviewForm professorId={lecturer.id} />
        </Paper>
      </Container>
    </Box>
  );
};

export default LecturerPage;
